import { ShippingPortalLabelSizes, ShippingPortalParcel } from '@lanterds/types';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    DialogContentText,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { useSnackbar, SnackbarKey } from 'notistack';
import { createFavorite, deleteFavorite, getParcelTypesByCustomer, updateFavorite } from 'src/api/shippingPortal';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { ShippingPortalFavorite } from 'src/util/types';
import './DealerContainer.scss';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { OutboundTabs } from '../types';
import { ColumnDef } from '@tanstack/react-table';
import { DebouncedTextField } from 'src/components/common/DebouncedInputs/DebouncedTextField';

export function DealerContainer({
    parcels = [],
    printLabels,
    showFavoritesDetails,
    setShowFavorites,
    handleReset,
    selectedFavorite,
    setSelectedFavorite,
    updateCurrentParcels,
    isPrinting,
    applyToAllParcels,
    setSelectedTab,
    fillHeight,
    refetchFavorites,
}: {
    parcels?: ShippingPortalParcel[];
    printLabels: (parcelsToGenerate: ShippingPortalParcel[]) => void;
    showFavoritesDetails?: boolean;
    setShowFavorites?: (value: boolean) => void;
    handleReset?: () => void;
    selectedFavorite?: ShippingPortalFavorite | null;
    setSelectedFavorite?: (favorite: ShippingPortalFavorite) => void;
    updateCurrentParcels: (index: number, updatedParcel: ShippingPortalParcel) => void;
    isPrinting: boolean;
    applyToAllParcels: (name: string, value: string | number) => void;
    setSelectedTab?: (value: number) => void;
    fillHeight?: boolean;
    refetchFavorites?: () => void;
}) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [selectedRows, setSelectedRows] = useState<any>({});
    const [parcelTypes, setParcelTypes] = useState<Record<string, { key: string; val: string }[]> | null>(null);
    const [parcelTypeMenuOptions, setParcelTypeMenuOptions] = useState<any>(<></>);
    const [nameDialogOpen, setNameDialogOpen] = React.useState(false);
    const [favoriteName, setFavoriteName] = React.useState('');
    const [isLoadingParcelTypes, setIsLoadingParcelTypes] = useState(false);
    const [dealerContainerParcels, setDealerContainerParcels] = useState<ShippingPortalParcel[]>(
        selectedFavorite ? selectedFavorite?.parcelsToPrint : parcels,
    );
    const [[commonLabelSize, isCommonLabelSizeUpdated], setCommonLabelSize] = useState<[string, boolean]>([
        ShippingPortalLabelSizes.LARGE,
        false,
    ]);
    const [[commonParcelType, isCommonParcelTypeUpdated], setCommonParcelType] = useState<[string, boolean]>(['LOOSE', false]);
    const [[commonReferenceNumber, isCommonReferenceNumberUpdated], setCommonReferenceNumber] = useState<[string, boolean]>([
        '',
        false,
    ]);
    const [[commonWeight, isCommonWeightUpdated], setCommonWeight] = useState<[number, boolean]>([0, false]);

    const { mutateAsync: createFavoriteAsync, isPending: isCreateFavoritePending } = createFavorite();
    const { mutateAsync: updateFavoriteAsync, isPending: isUpdateFavoritePending } = updateFavorite();
    const { mutateAsync: deleteFavoriteAsync, isPending: isDeletePending } = deleteFavorite();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowsToDelete, setRowsToDelete] = useState<string[]>([]);
    const deleteSnackbarRef = useRef<SnackbarKey | null>(null);
    const saveSnackbarRef = useRef<SnackbarKey | null>(null);
    const printSnackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        const tempParcels = selectedFavorite ? selectedFavorite?.parcelsToPrint : parcels;

        const customerIds = new Set(tempParcels.map((parcel) => parcel.fmtpCustomerId));

        const types = parcelTypes ?? {};

        for (const id of customerIds.values()) {
            if (id && id !== 'undefined' && !(id in types)) {
                setIsLoadingParcelTypes(true);
                getParcelTypesByCustomer(id).then((response: string[]) => {
                    const mappedTypes = response.map((t) => {
                        return {
                            key: t,
                            val: t,
                        };
                    });

                    setParcelTypeMenuOptions(
                        mappedTypes.map((type) => {
                            return (
                                <MenuItem disableRipple key={type.key} value={type.val}>
                                    {type.val}
                                </MenuItem>
                            );
                        }),
                    );

                    setParcelTypes({ ...types, [id]: mappedTypes });
                    setIsLoadingParcelTypes(false);
                });
            }
        }

        setDealerContainerParcels(tempParcels);
    }, [selectedFavorite, parcels]);

    const handleClose = () => {
        setFavoriteName('');
        setNameDialogOpen(false);
    };

    const handleOnNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        setFavoriteName(value);
    };

    const handleOnSubmitAndPrint = () => {
        printLabels(dealerContainerParcels);
    };

    const handleOnPrintSelected = async () => {
        const rows = Object.keys(selectedRows);

        const tempParcels = dealerContainerParcels.filter((parcel, index) => {
            return rows.includes(String(index));
        });

        printSnackbarRef.current = enqueueSnackbar('Printing selected labels…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        try {
            await printLabels(tempParcels);
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to print selected labels.', { variant: 'error' });
        } finally {
            enqueueSnackbar('Print job Finished.', { variant: 'success' });
            if (printSnackbarRef.current) {
                closeSnackbar(printSnackbarRef.current);
                printSnackbarRef.current = null;
            }
        }
    };

    const handleOnCancel = () => {
        if (setShowFavorites) setShowFavorites(true);
    };

    const handleOnAddFavorite = async () => {
        await createFavoriteAsync({
            name: favoriteName,
            labelQuantity: parcels.length,
            dealerCount: new Set(parcels.map((parcel) => parcel.destination.id)).size,
            parcelsToPrint: parcels,
        });

        if (handleReset) {
            handleReset();
        }
    };

    const handleOnSaveFavorite = async () => {
        saveSnackbarRef.current = enqueueSnackbar('Updating favorite…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });
        try {
            if (selectedFavorite && selectedFavorite.id) {
                await updateFavoriteAsync(selectedFavorite);

                if (setShowFavorites) setShowFavorites(true);
            }
        } catch (err) {
            enqueueSnackbar('Failed to update favorite.', { variant: 'error' });
        } finally {
            if (saveSnackbarRef.current) {
                closeSnackbar(saveSnackbarRef.current);
                saveSnackbarRef.current = null;
            }
        }
    };

    const handleOnDeleteParcel = () => {
        const rows = Object.keys(selectedRows);
        if (!rows.length) return;
        setRowsToDelete(rows);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteParcels = async () => {
        setDeleteDialogOpen(false);
        if (!selectedFavorite?.id) return;

        deleteSnackbarRef.current = enqueueSnackbar('Deleting destinations…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        try {
            // filter out the rows in rowsToDelete
            const tempParcels = selectedFavorite.parcelsToPrint.filter((_, idx) => !rowsToDelete.includes(String(idx)));

            // prepare updated favorite
            const tempFavorite: ShippingPortalFavorite = {
                id: selectedFavorite.id,
                name: selectedFavorite.name,
                labelQuantity: tempParcels.length,
                dealerCount: new Set(tempParcels.map((p) => p.destination.id)).size,
                parcelsToPrint: tempParcels,
            };

            // if no parcels left, delete the favorite entirely
            if (tempParcels.length === 0) {
                await deleteFavoriteAsync(selectedFavorite.id);
                if (refetchFavorites) {
                    refetchFavorites();
                }
                setShowFavorites?.(true);
            } else {
                // otherwise update it
                await updateFavoriteAsync(tempFavorite);
                setSelectedFavorite?.(tempFavorite);
            }

            // clear selection
            setSelectedRows({});
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to delete destinations.', { variant: 'error' });
        } finally {
            if (deleteSnackbarRef.current) {
                closeSnackbar(deleteSnackbarRef.current);
                deleteSnackbarRef.current = null;
            }
        }
    };

    let columns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            header: 'Destination',
            meta: {
                innerHtml: (props: any) => {
                    const { row } = props;
                    const { original } = row;

                    return <>{`${original.destination.name} (${original.destination.locationCode})`}</>;
                },
            },
        },
        {
            accessorKey: 'labelSize',
            header: 'Label Size',
            size: 80,
            meta: {
                innerHtml: (props: any) => {
                    const { row, getValue } = props;
                    const { original: parcel, index } = row;
                    const { name } = parcel;
                    const initialValue = isCommonLabelSizeUpdated ? commonLabelSize : getValue();
                    // We need to keep and update the state of the cell normally
                    const [value, setValue] = React.useState(initialValue);

                    // When the input is blurred, we'll make changes to the data
                    const onBlur = () => {
                        updateCurrentParcels(index, { ...parcel, labelSize: value });
                        const tempParcels = [...dealerContainerParcels];
                        tempParcels[index] = { ...parcel, labelSize: value };
                        setDealerContainerParcels(tempParcels);
                        setCommonLabelSize([commonLabelSize, false]);
                    };

                    // If the initialValue is changed external, sync it up with our state
                    React.useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    return (
                        <Box key={`${name}-${index}-container`} id={`${name}-${index}-container`}>
                            <Select
                                key={`${name}-${index}-select`}
                                id={`${name}-${index}-select`}
                                variant="outlined"
                                fullWidth
                                onChange={(event) => setValue(event.target.value as ShippingPortalLabelSizes)}
                                onBlur={onBlur}
                                value={value}
                                placeholder={value}
                                defaultValue={value}
                            >
                                <MenuItem disableRipple value={ShippingPortalLabelSizes.LARGE}>
                                    {ShippingPortalLabelSizes.LARGE}
                                </MenuItem>
                                <MenuItem disableRipple value={ShippingPortalLabelSizes.MEDIUM}>
                                    {ShippingPortalLabelSizes.MEDIUM}
                                </MenuItem>
                                <MenuItem disableRipple value={ShippingPortalLabelSizes.SMALL}>
                                    {ShippingPortalLabelSizes.SMALL}
                                </MenuItem>
                            </Select>
                        </Box>
                    );
                },
            },
        },
        {
            accessorKey: 'parcelType',
            header: 'Container Type',
            size: 80,
            meta: {
                innerHtml: (props: any) => {
                    const { row, getValue } = props;
                    const { original: parcel, index } = row;
                    const { name } = parcel;
                    const initialValue = isCommonParcelTypeUpdated ? commonParcelType : getValue();
                    // We need to keep and update the state of the cell normally
                    const [value, setValue] = React.useState(initialValue);

                    // When the input is blurred, we'll make changes to the data
                    const onBlur = () => {
                        updateCurrentParcels(index, { ...parcel, parcelType: value });
                        const tempParcels = [...dealerContainerParcels];
                        tempParcels[index] = { ...parcel, parcelType: value };
                        setDealerContainerParcels(tempParcels);
                        setCommonParcelType([commonParcelType, false]);
                    };

                    // If the initialValue is changed external, sync it up with our state
                    React.useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    return (
                        <Box key={`${name}-${index}-container`} id={`${name}-${index}-container`}>
                            <Select
                                key={`${name}-${index}-select`}
                                id={`${name}-${index}-select`}
                                variant="outlined"
                                fullWidth
                                onChange={(event) => setValue(event.target.value)}
                                onBlur={onBlur}
                                value={value}
                                placeholder={value}
                                defaultValue={value}
                                disabled={isLoadingParcelTypes}
                                endAdornment={
                                    <>
                                        {isLoadingParcelTypes ? (
                                            <CircularProgress className="parcel-type-loading" color="inherit" size={20} />
                                        ) : null}
                                    </>
                                }
                            >
                                {parcelTypeMenuOptions}
                            </Select>
                        </Box>
                    );
                },
            },
        },
        {
            accessorKey: 'reference',
            header: 'Reference No.',
            size: 80,
            meta: {
                innerHtml: (props: any) => {
                    const { row, getValue } = props;
                    const { original: parcel, index } = row;
                    const initialValue = isCommonReferenceNumberUpdated ? commonReferenceNumber : getValue();

                    // We need to keep and update the state of the cell normally
                    const [value, setValue] = React.useState(initialValue);

                    // When the input is blurred, we'll make changes to the data
                    const onBlur = () => {
                        updateCurrentParcels(index, { ...parcel, reference: value });
                        const tempParcels = [...dealerContainerParcels];
                        tempParcels[index] = { ...parcel, reference: value };
                        setDealerContainerParcels(tempParcels);
                        setCommonReferenceNumber([commonReferenceNumber, false]);
                    };

                    // If the initialValue is changed external, sync it up with our state
                    React.useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    return (
                        <TextField
                            autoComplete="off"
                            variant="outlined"
                            onChange={(e) => setValue(e.target.value)}
                            onBlur={onBlur}
                            fullWidth
                            value={value}
                            InputProps={{
                                inputProps: { min: 0 },
                            }}
                        />
                    );
                },
            },
        },
        {
            accessorKey: 'weight',
            header: 'Weight',
            size: 80,
            meta: {
                innerHtml: (props: any) => {
                    const { row, getValue } = props;
                    const { original: parcel, index } = row;
                    const initialValue = isCommonWeightUpdated ? commonWeight : getValue();
                    // We need to keep and update the state of the cell normally
                    const [value, setValue] = React.useState(initialValue);

                    // When the input is blurred, we'll make changes to the data
                    const onBlur = () => {
                        const weight = value ? Number(value) : 0;
                        updateCurrentParcels(index, { ...parcel, weight });
                        const tempParcels = [...dealerContainerParcels];
                        tempParcels[index] = { ...parcel, weight };
                        setDealerContainerParcels(tempParcels);
                        setCommonWeight([commonWeight, false]);
                    };

                    // If the initialValue is changed external, sync it up with our state
                    React.useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        const newValue = Number(event.target.value);
                        setValue(newValue);
                    };

                    return (
                        <TextField
                            type="number"
                            variant="outlined"
                            onChange={handleOnChange}
                            onBlur={onBlur}
                            fullWidth
                            value={value}
                            InputProps={{
                                inputProps: { min: 0 },
                            }}
                            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                        />
                    );
                },
            },
        },
        {
            accessorKey: 'weightUnit',
            header: 'Weight Unit',
            size: 80,
            meta: {
                innerHtml: (props: any) => {
                    const { row } = props;
                    const { original, index } = row;
                    const { name } = original;

                    const value = 'pounds';

                    return (
                        <Box key={`${name}-${index}-container`} id={`${name}-${index}-container`}>
                            <Select
                                key={`${name}-${index}-select`}
                                id={`${name}-${index}-select`}
                                variant="outlined"
                                fullWidth
                                value={value}
                                placeholder={value}
                                defaultValue={value}
                            >
                                <MenuItem disableRipple value={'pounds'}>
                                    Pounds
                                </MenuItem>
                            </Select>
                        </Box>
                    );
                },
            },
        },
    ];

    if (showFavoritesDetails) {
        columns = [
            {
                id: 'select',
                size: 20,
                enableColumnFilter: false,
            },
            ...columns,
        ];
    }

    return (
        <>
            <Box
                id={`dealer-container${showFavoritesDetails ? '-favorite-details' : ''}`}
                className={`${fillHeight ? 'auto-fill-height' : ''}`}
            >
                <CardHeader
                    title={selectedFavorite?.name ?? ''}
                    sx={{ display: `${selectedFavorite?.name ? 'block' : 'none'}` }}
                />
                <CardContent className={`${fillHeight ? 'auto-fill-height' : ''}`}>
                    <FormControl fullWidth className="apply-to-all-select">
                        <InputLabel id="apply-all-select-label" shrink={false}>
                            Apply to All...
                        </InputLabel>
                        <Select
                            id="apply-to-all-select"
                            labelId="apply-all-select-label"
                            variant="outlined"
                            fullWidth
                            MenuProps={{
                                className: 'apply-all-quantities-menu',
                                MenuListProps: {
                                    disabledItemsFocusable: false,
                                },
                            }}
                        >
                            <MenuItem key={'labelSize'} disableRipple>
                                <Box
                                    className="menu-item-container"
                                    onClick={(event) => event.stopPropagation()} // Prevent closing
                                >
                                    <Typography className="menu-item-label">Label Size</Typography>
                                    <InputLabel id="common-label-size-select-label" shrink={false}></InputLabel>
                                    <Select
                                        name="labelSize"
                                        labelId="common-label-size-select-label"
                                        variant="outlined"
                                        onChange={(event) => {
                                            const newValue = event.target.value as ShippingPortalLabelSizes;
                                            applyToAllParcels('labelSize', newValue);
                                            setCommonLabelSize([newValue, true]);
                                        }}
                                        fullWidth
                                        onClick={(event) => event.stopPropagation()} // Prevent closing
                                        onFocus={(event) => event.stopPropagation()} // Prevent hover over styles
                                        value={commonLabelSize}
                                    >
                                        <MenuItem disableRipple value={ShippingPortalLabelSizes.LARGE}>
                                            {ShippingPortalLabelSizes.LARGE}
                                        </MenuItem>
                                        <MenuItem disableRipple value={ShippingPortalLabelSizes.MEDIUM}>
                                            {ShippingPortalLabelSizes.MEDIUM}
                                        </MenuItem>
                                        <MenuItem disableRipple value={ShippingPortalLabelSizes.SMALL}>
                                            {ShippingPortalLabelSizes.SMALL}
                                        </MenuItem>
                                    </Select>
                                </Box>
                            </MenuItem>

                            <MenuItem disableRipple>
                                <Box
                                    className="menu-item-container"
                                    onClick={(event) => event.stopPropagation()} // Prevent closing
                                >
                                    <Typography className="menu-item-label">Container Type</Typography>
                                    <InputLabel id="common-parcel-type-select-label" shrink={false}></InputLabel>
                                    <Select
                                        name="parcelType"
                                        variant="outlined"
                                        labelId="common-parcel-type-select-label"
                                        onChange={(event) => {
                                            const newValue = event.target.value;
                                            applyToAllParcels('parcelType', newValue);
                                            setCommonParcelType([newValue, true]);
                                        }}
                                        fullWidth
                                        onClick={(event) => event.stopPropagation()} // Prevent closing
                                        onFocus={(event) => event.stopPropagation()} // Prevent hover over styles
                                        value={commonParcelType}
                                    >
                                        {parcelTypeMenuOptions}
                                    </Select>
                                </Box>
                            </MenuItem>
                            <MenuItem disableRipple>
                                <Box
                                    className="menu-item-container"
                                    onClick={(event) => event.stopPropagation()} // Prevent closing
                                >
                                    <Typography className="menu-item-label">Reference No.</Typography>
                                    <DebouncedTextField
                                        name="reference"
                                        variant="outlined"
                                        onDebounceChange={(value) => {
                                            applyToAllParcels('reference', value);
                                            setCommonReferenceNumber([value, true]);
                                        }}
                                        fullWidth
                                        onClick={(event) => event.stopPropagation()} // Prevent closing
                                        onFocus={(event) => event.stopPropagation()} // Prevent hover over styles
                                        debounceValue={commonReferenceNumber}
                                        InputProps={{
                                            inputProps: { min: 0 },
                                        }}
                                        autoComplete="off"
                                    />
                                </Box>
                            </MenuItem>
                            <MenuItem disableRipple>
                                <Box
                                    className="menu-item-container"
                                    onClick={(event) => event.stopPropagation()} // Prevent closing
                                >
                                    <Typography className="menu-item-label">Weight</Typography>

                                    <DebouncedTextField
                                        name="weight"
                                        type="number"
                                        variant="outlined"
                                        onDebounceChange={(value) => {
                                            const newValue = Number(value);
                                            applyToAllParcels('weight', newValue);
                                            setCommonWeight([newValue, true]);
                                        }}
                                        fullWidth
                                        onClick={(event) => event.stopPropagation()} // Prevent closing
                                        onFocus={(event) => event.stopPropagation()} // Prevent hover over styles
                                        debounceValue={commonWeight.toString()}
                                        InputProps={{
                                            inputProps: { min: 0 },
                                        }}
                                    />
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FmtpTable
                        isFetching={isCreateFavoritePending || isUpdateFavoritePending}
                        loadingIndicatorMessage={isCreateFavoritePending ? 'Creating Favorite...' : 'Updating Favorite'}
                        data={dealerContainerParcels}
                        columns={columns}
                        setSelectedRows={setSelectedRows}
                        selectedRows={selectedRows}
                    />
                    <Grid xs={12} sm={6} className="buttons">
                        <CardActions>
                            <>
                                {!showFavoritesDetails ? (
                                    <Box>
                                        <Button variant="contained" className="submit-button" onClick={handleOnSubmitAndPrint}>
                                            {isPrinting ? (
                                                <>
                                                    Generating... <LoadingIndicator message="" />
                                                </>
                                            ) : (
                                                <>Submit / Print Label</>
                                            )}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            className="submit-button"
                                            onClick={() => setNameDialogOpen(true)}
                                        >
                                            Add to Favorites
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Button
                                            variant="contained"
                                            className="submit-button"
                                            onClick={handleOnDeleteParcel}
                                            disabled={!Object.keys(selectedRows).length}
                                        >
                                            Delete Destinations
                                        </Button>
                                        <Button
                                            variant="contained"
                                            className="submit-button"
                                            onClick={() => handleOnSaveFavorite()}
                                        >
                                            Update Favorite
                                        </Button>
                                        <Button
                                            variant="contained"
                                            className="submit-button"
                                            onClick={handleOnPrintSelected}
                                            disabled={!Object.keys(selectedRows).length}
                                        >
                                            {isPrinting ? (
                                                <>
                                                    Generating... <LoadingIndicator message="" />
                                                </>
                                            ) : (
                                                <>Print Selected</>
                                            )}
                                        </Button>
                                        <Button variant="contained" className="submit-button" onClick={handleOnCancel}>
                                            Cancel
                                        </Button>
                                    </Box>
                                )}
                            </>
                        </CardActions>
                    </Grid>
                </CardContent>
            </Box>
            <Dialog
                open={nameDialogOpen}
                onClose={handleClose}
                PaperProps={{
                    component: 'form',
                    onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
                        event.preventDefault();
                        if (favoriteName) {
                            handleClose();
                            await handleOnAddFavorite();
                            if (setSelectedTab) {
                                setSelectedTab(OutboundTabs.Favorites);
                            }
                        }
                    },
                }}
            >
                <DialogTitle>Add to Favorites</DialogTitle>
                <DialogContent id="dialog-content">
                    <TextField
                        id="favorite-name"
                        label="Name This List"
                        variant="outlined"
                        name="favorite-name"
                        onChange={handleOnNameChange}
                        autoComplete="off"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit" disabled={!favoriteName} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Destinations</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected destinations? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmDeleteParcels}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
